import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useRipple } from '@/hooks/utils';
import { cn } from '@/lib/utils';

import { SpinnerLoader } from '../Loading';
import { Icon } from './icon';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center overflow-hidden whitespace-nowrap rounded-full font-medium leading-none ring-offset-background transition-[transform,colors,background-color,box-shadow] duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none active:scale-[0.97] motion-reduce:active:scale-100 outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        // Default variant (grayscale)
        'default': 'bg-action text-background hover:bg-action-hover active:bg-action-selected [&_svg]:text-background',

        // Text variants
        'text': 'text-foreground hover:bg-foreground/8 active:bg-foreground/12 disabled:text-foreground/50',
        'text-primary': 'text-primary hover:bg-primary/8 active:bg-primary/12',
        'text-secondary': 'text-muted-foreground hover:bg-foreground/8 active:bg-foreground/12',

        // MD3 filled (= existing primary)
        'primary': 'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-dark',
        'primary-light': 'bg-primary-light text-primary-foreground hover:bg-primary active:bg-primary-dark',
        'primary-dark': 'bg-primary-dark text-primary-foreground hover:bg-primary active:bg-primary-light',

        // MD3 tonal — high-emphasis without primary fill (uses primary-container)
        'tonal': 'bg-primary-container text-on-primary-container hover:bg-primary-container/80 active:bg-primary-container/70',
        'tonal-secondary': 'bg-secondary-container text-on-secondary-container hover:bg-secondary-container/80 active:bg-secondary-container/70',
        'tonal-tertiary': 'bg-tertiary-container text-on-tertiary-container hover:bg-tertiary-container/80 active:bg-tertiary-container/70',
        'tonal-success': 'bg-success-container text-on-success-container hover:bg-success-container/80 active:bg-success-container/70',
        'tonal-warning': 'bg-warning-container text-on-warning-container hover:bg-warning-container/80 active:bg-warning-container/70',
        'tonal-error': 'bg-error-container text-on-error-container hover:bg-error-container/80 active:bg-error-container/70',
        'tonal-info': 'bg-info-container text-on-info-container hover:bg-info-container/80 active:bg-info-container/70',

        // MD3 elevated — surface-1 background, Z1 shadow, primary text
        'elevated': 'bg-surface-1 text-primary shadow-[var(--elev-1)] hover:shadow-[var(--elev-2)] hover:bg-surface-2 active:bg-surface-3',

        // Secondary variants
        'secondary': 'bg-secondary text-secondary-foreground hover:bg-secondary-dark active:bg-secondary-light',
        'secondary-light': 'bg-secondary-light text-secondary-foreground hover:bg-secondary active:bg-secondary-dark',
        'secondary-dark': 'bg-secondary-dark text-secondary-foreground hover:bg-secondary active:bg-secondary-light',

        // Info variants
        'info': 'bg-info text-info-foreground hover:bg-info-dark active:bg-info-light',
        'info-light': 'bg-info-light text-info-foreground hover:bg-info active:bg-info-dark',
        'info-dark': 'bg-info-dark text-info-foreground hover:bg-info active:bg-info-light',

        // Success variants
        'success': 'bg-success text-success-foreground hover:bg-success-dark active:bg-success-light',
        'success-light': 'bg-success-light text-success-foreground hover:bg-success active:bg-success-dark',
        'success-dark': 'bg-success-dark text-success-foreground hover:bg-success active:bg-success-light',

        // Warning variants
        'warning': 'bg-warning text-warning-foreground hover:bg-warning-dark active:bg-warning-light',
        'warning-light': 'bg-warning-light text-warning-foreground hover:bg-warning active:bg-warning-dark',
        'warning-dark': 'bg-warning-dark text-warning-foreground hover:bg-warning active:bg-warning-light',

        // Error variants
        'error': 'bg-error text-error-foreground hover:bg-error-dark active:bg-error-light',
        'text-error': 'text-error hover:bg-error/8 active:bg-error/12',
        'error-light': 'bg-error-light text-error-foreground hover:bg-error active:bg-error-dark',
        'error-dark': 'bg-error-dark text-error-foreground hover:bg-error active:bg-error-light',

        // Action variants
        'action': 'bg-action text-background hover:bg-action-hover active:bg-action-selected',
        'action-selected': 'bg-action-selected text-primary hover:bg-action-hover',

        // MD3 outlined — 1px outline-variant border, no fill, primary text
        'outline': 'border border-outline-variant bg-transparent text-foreground hover:bg-foreground/8 active:bg-foreground/12',
        'outline-primary': 'border border-primary text-primary hover:bg-primary/8 active:bg-primary/12',
        'outline-secondary': 'border border-secondary text-secondary hover:bg-secondary/8 active:bg-secondary/12',
        'outline-info': 'border border-info text-info hover:bg-info/8 active:bg-info/12',
        'outline-success': 'border border-success text-success hover:bg-success/8 active:bg-success/12',
        'outline-warning': 'border border-warning text-warning hover:bg-warning/8 active:bg-warning/12',
        'outline-error': 'border border-error text-error hover:bg-error/8 active:bg-error/12',

        // Ghost variants
        'ghost': 'text-muted-foreground hover:bg-foreground/8 active:bg-foreground/12',
        'ghost-primary': 'text-primary hover:bg-primary/8 hover:text-primary active:bg-primary/12 active:text-primary',
        'ghost-secondary': 'text-secondary hover:bg-secondary/8 hover:text-secondary active:bg-secondary/12 active:text-secondary',
        'ghost-info': 'text-info hover:bg-info/8 hover:text-info active:bg-info/12 active:text-info',
        'ghost-success': 'text-success hover:bg-success/8 hover:text-success active:bg-success/12 active:text-success',
        'ghost-warning': 'text-warning hover:bg-warning/8 hover:text-warning active:bg-warning/12 active:text-warning',
        'ghost-error': 'text-error hover:bg-error/8 hover:text-error active:bg-error/12 active:text-error',

        // Link variants
        'link': 'text-primary underline-offset-4 hover:underline',
      },
      // Heights track the Cohort spec button scale: sm 28 / md (default) 32
      // / lg 38. The default now matches the Input height (h-8) so a button
      // and a field sit flush on the same row. Icon sizes mirror the label
      // sizes one-for-one.
      size: {
        'default': 'h-8 px-3.5 py-0 text-sm',
        'sm': 'h-7 px-3 py-0 text-xs',
        'lg': 'h-[38px] px-[18px] py-0 text-sm',
        'icon': 'size-8 p-0',
        'icon-sm': 'size-7 p-0',
        'icon-xs': 'size-6 p-0',
        'icon-lg': 'size-[38px] p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = {
  asChild?: boolean;
  loading?: boolean;
  startIcon?: string;
  endIcon?: string;
  disableRipple?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  tooltipAlign?: 'start' | 'center' | 'end';
} & React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

function Button({
  ref,
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  startIcon,
  endIcon,
  disabled = false,
  disableRipple = false,
  tooltipContent,
  tooltipSide = 'top',
  tooltipAlign = 'center',
  onPointerDown,
  onKeyDown,
  children,
  ...props
}: ButtonProps & { ref?: React.RefObject<HTMLButtonElement | null> }) {
  const isDisabled = loading === true || disabled === true;

  // Icon-only: a size starting with 'icon' AND no label/children rendered alongside the icon.
  const hasChildrenContent = React.Children.toArray(children).some(child =>
    (typeof child === 'string' && child.trim().length > 0)
    || typeof child === 'number'
    || React.isValidElement(child),
  );
  const isIconSize = typeof size === 'string' && size.startsWith('icon') === true;
  const isIconOnly = isIconSize === true && hasChildrenContent === false;
  // Tighter gap (6px) keeps icon and label optically bound; the CVA padding
  // stays symmetric so the group is centered as a unit inside the button.
  const layoutClass = isIconOnly === true ? 'inline-flex items-center justify-center' : 'inline-flex items-center gap-1.5';

  // Development warning: icon-only buttons must have an accessible name.
  if (process.env.NODE_ENV !== 'production' && isIconSize === true) {
    const hasAriaLabel = typeof props['aria-label'] === 'string' && props['aria-label'].length > 0;
    const hasAriaLabelledBy = typeof props['aria-labelledby'] === 'string' && props['aria-labelledby'].length > 0;
    const hasTitle = typeof props.title === 'string' && props.title.length > 0;
    const hasTooltip = tooltipContent !== undefined && tooltipContent !== null;
    if (hasAriaLabel === false && hasAriaLabelledBy === false && hasTitle === false && hasTooltip === false && hasChildrenContent === false) {
      // eslint-disable-next-line no-console
      console.warn('[Button] icon-only button is missing an accessible name. Provide aria-label, tooltipContent, or sr-only text.');
    }
  }

  // Glyph sizes track the Cohort spec: label buttons render icons at 14 (sm)
  // / 16 (default), icon-only buttons at 16-20. The "16 everywhere" rule from
  // DESIGN.md — no oversized 20/24px glyphs crowding a 32px button.
  const iconSize = {
    'icon-xs': 16,
    'icon-sm': 16,
    'icon': 18,
    'icon-lg': 20,
    'sm': 14,
    'default': 16,
    'lg': 18,
  }[size === undefined || size === null ? 'default' : size];

  const { rippleProps } = useRipple({ disabled: disableRipple === true || isDisabled === true });

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      rippleProps.onPointerDown(event);
      if (onPointerDown !== undefined) {
        onPointerDown(event);
      }
    },
    [rippleProps, onPointerDown],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      rippleProps.onKeyDown(event);
      if (onKeyDown !== undefined) {
        onKeyDown(event);
      }
    },
    [rippleProps, onKeyDown],
  );

  const buttonContent = asChild === true
    ? (
        <Slot
          className={cn(
            buttonVariants({ variant, size, className }),
            isDisabled === true && 'disabled',
            layoutClass,
          )}
          ref={ref}
          aria-busy={loading === true ? true : undefined}
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          {...props}
        >
          {React.isValidElement(children)
            ? React.cloneElement(children as React.ReactElement<{ className?: string; children?: React.ReactNode }>, {
              className: cn(
                (children as React.ReactElement<{ className?: string; children?: React.ReactNode }>).props?.className,
                'relative inline-flex items-center justify-center gap-1.5',
              ),
              children: (
                <>
                  {startIcon !== undefined && startIcon !== null && loading === false && (
                    <Icon
                      icon={startIcon}
                      size={iconSize}
                      className="shrink-0 text-[currentColor]"
                    />
                  )}
                  {loading === true && (
                    <SpinnerLoader
                      variant="inherit"
                      size={iconSize}
                    />
                  )}
                  {(children as React.ReactElement<{ className?: string; children?: React.ReactNode }>).props?.children}
                  {endIcon !== undefined && endIcon !== null && loading === false && (
                    <Icon
                      icon={endIcon}
                      size={iconSize}
                      className="shrink-0 text-[currentColor]"
                    />
                  )}
                </>
              ),
            })
            : children}
        </Slot>
      )
    : (
        <button
          type="button"
          disabled={isDisabled}
          aria-busy={loading === true ? true : undefined}
          className={cn(
            buttonVariants({ variant, size, className }),
            isDisabled === true && 'disabled',
            layoutClass,
          )}
          ref={ref}
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          {...props}
        >
          {startIcon !== undefined && startIcon !== null && loading === false && (
            <Icon
              icon={startIcon}
              size={iconSize}
              className="shrink-0 text-[currentColor]"
            />
          )}
          {loading === true && (
            <SpinnerLoader
              variant="inherit"
              size={iconSize}
            />
          )}
          {children}
          {endIcon !== undefined && endIcon !== null && loading === false && (
            <Icon
              icon={endIcon}
              size={iconSize}
              className="shrink-0 text-[currentColor]"
            />
          )}
        </button>
      );

  // If tooltipContent is provided, wrap button in Tooltip
  if (tooltipContent !== undefined && tooltipContent !== null) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{buttonContent}</span>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide} align={tooltipAlign}>
            {typeof tooltipContent === 'string'
              ? (
                  <p className="whitespace-pre-line">{tooltipContent}</p>
                )
              : (
                  tooltipContent
                )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonContent;
}

Button.displayName = 'Button';

export { Button, buttonVariants };
